import type { ReactNode } from 'react';
import {
  Dialog as AriaDialog,
  DialogTrigger,
  Heading,
  Modal,
  ModalOverlay,
} from 'react-aria-components';
import { cn } from './cn.ts';

export { DialogTrigger };

export interface DialogProps {
  title: string;
  description?: ReactNode;
  /** `close` is React Aria's own, so Escape, the overlay, and this agree. */
  children: (close: () => void) => ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl' } as const;

/**
 * The dialog is React Aria's, not a div with a backdrop: focus trapping, the
 * `aria-modal` wiring, Escape handling, scroll locking, and returning focus to
 * the trigger are all behaviour we would otherwise be reimplementing badly.
 * That is the reason ratified decision 10 names this library at all.
 */
export function Dialog({
  title,
  description,
  children,
  isOpen,
  onOpenChange,
  size = 'md',
  className,
}: DialogProps) {
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      className="fixed inset-0 z-50 flex min-h-full items-center justify-center overflow-y-auto bg-overlay p-4 backdrop-blur-[2px]"
    >
      <Modal
        className={cn(
          'w-full rounded-xl border border-secondary bg-primary shadow-xl outline-none',
          SIZES[size],
          className,
        )}
      >
        <AriaDialog className="outline-none">
          {({ close }) => (
            <div className="flex flex-col">
              <header className="border-b border-secondary px-6 py-5">
                <Heading slot="title" className="text-lg font-semibold text-primary">
                  {title}
                </Heading>
                {description ? <p className="mt-1 text-sm text-tertiary">{description}</p> : null}
              </header>
              {children(close)}
            </div>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

export function DialogBody({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4 px-6 py-5">{children}</div>;
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <footer className="flex items-center justify-end gap-3 border-t border-secondary px-6 py-4">
      {children}
    </footer>
  );
}
